<template>
  <transition name="broker-reconnect-fade">
    <div v-if="visible" class="broker-reconnect-overlay" @click.self="dismiss">
      <div class="broker-reconnect-modal" :class="{ dark: isDark }">
        <button class="broker-reconnect-close" @click="dismiss" aria-label="Close">&times;</button>

        <!-- Session Expired - Reconnect -->
        <div v-if="mode === 'reconnect'" class="broker-reconnect-body">
          <div class="broker-reconnect-icon warning">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <h3 class="broker-reconnect-title">{{ brokerName }} Session Expired</h3>
          <p class="broker-reconnect-desc">
            Your {{ brokerName }} connection has expired. Reconnect to continue receiving real-time market data.
          </p>
          <div class="broker-reconnect-actions">
            <button class="broker-reconnect-btn primary" @click="reconnect" :disabled="reconnecting">
              <span v-if="reconnecting">Reconnecting...</span>
              <span v-else>Reconnect Now</span>
            </button>
            <button class="broker-reconnect-btn secondary" @click="dismiss" :disabled="reconnecting">
              Not Now
            </button>
          </div>
        </div>

        <!-- First Time - Connect -->
        <div v-else-if="mode === 'connect'" class="broker-reconnect-body">
          <div class="broker-reconnect-icon info">
            <svg width="40" height="40" viewBox="0 0 48 48" fill="none">
              <rect width="48" height="48" rx="12" fill="#2962ff"/>
              <path d="M24 12C17.3726 12 12 17.3726 12 24C12 30.6274 17.3726 36 24 36C30.6274 36 36 30.6274 36 24C36 17.3726 30.6274 12 24 12Z" fill="white"/>
              <path d="M24 18C20.6863 18 18 20.6863 18 24C18 27.3137 20.6863 30 24 30C27.3137 30 30 27.3137 30 24C30 20.6863 27.3137 18 24 18Z" fill="#2962ff"/>
            </svg>
          </div>
          <h3 class="broker-reconnect-title">Connect {{ brokerName }}</h3>
          <p class="broker-reconnect-desc">
            Get real-time market data with professional charting. Connect your {{ brokerName }} account to unlock live data feeds.
          </p>
          <div class="broker-reconnect-actions">
            <button class="broker-reconnect-btn primary" @click="connect">
              Connect {{ brokerName }}
            </button>
            <button class="broker-reconnect-btn secondary" @click="dismiss">
              Cancel
            </button>
          </div>
        </div>

        <!-- Reconnecting State -->
        <div v-else-if="mode === 'reconnecting'" class="broker-reconnect-body">
          <div class="broker-reconnect-icon loading">
            <div class="loading-spinner"></div>
          </div>
          <h3 class="broker-reconnect-title">Reconnecting to {{ brokerName }}</h3>
          <p class="broker-reconnect-desc">
            Please wait while we re-establish your {{ brokerName }} connection...
          </p>
          <div class="broker-reconnect-progress">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: progress + '%' }"></div>
            </div>
          </div>
        </div>

        <!-- Success State -->
        <div v-else-if="mode === 'success'" class="broker-reconnect-body">
          <div class="broker-reconnect-icon success">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>
          <h3 class="broker-reconnect-title">Successfully Connected!</h3>
          <p class="broker-reconnect-desc">
            Your {{ brokerName }} connection has been restored. Real-time data will resume shortly.
          </p>
          <div class="broker-reconnect-actions">
            <button class="broker-reconnect-btn primary" @click="dismiss">
              Done
            </button>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="mode === 'error'" class="broker-reconnect-body">
          <div class="broker-reconnect-icon error">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          <h3 class="broker-reconnect-title">Connection Failed</h3>
          <p class="broker-reconnect-desc">
            {{ errorMessage || 'Failed to connect to ' + brokerName + '. Please try again.' }}
          </p>
          <div class="broker-reconnect-actions">
            <button class="broker-reconnect-btn primary" @click="retry">
              Try Again
            </button>
            <button class="broker-reconnect-btn secondary" @click="dismiss">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import FyersService from '../services/fyers_service.js';
import UpstoxService from '../services/upstox_service.js';
import AliceBlueService from '../services/aliceblue_service.js';
import DhanService from '../services/dhan_service.js';
import TrueDataService from '../services/truedata_service.js';
import GlobalDataFeedsService from '../services/globaldatafeeds_service.js';

export default {
  name: 'BrokerReconnectModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    broker: {
      type: String,
      default: 'fyers' // fyers, upstox, aliceblue, dhan, truedata, globaldatafeeds
    },
    isDark: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      mode: 'reconnect', // reconnect, connect, reconnecting, success, error
      reconnecting: false,
      progress: 0,
      errorMessage: '',
      progressInterval: null
    };
  },
  computed: {
    brokerName() {
      const names = {
        fyers: 'Fyers',
        upstox: 'Upstox',
        aliceblue: 'AliceBlue',
        dhan: 'DhanHQ',
        truedata: 'TrueData',
        globaldatafeeds: 'Global Data Feeds'
      };
      return names[this.broker] || 'Broker';
    },
    brokerService() {
      const services = {
        fyers: FyersService,
        upstox: UpstoxService,
        aliceblue: AliceBlueService,
        dhan: DhanService,
        truedata: TrueDataService,
        globaldatafeeds: GlobalDataFeedsService
      };
      return services[this.broker];
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.checkConnectionStatus();
      } else {
        this.reset();
      }
    }
  },
  methods: {
    async checkConnectionStatus() {
      try {
        const validation = await this.brokerService.validateSession();
        if (validation.valid) {
          this.mode = 'success';
        } else if (validation.expired) {
          this.mode = 'reconnect';
        } else {
          this.mode = 'connect';
        }
      } catch (error) {
        console.error(`[${this.brokerName}] Error checking connection status:`, error);
        this.mode = 'connect';
      }
    },
    
    async reconnect() {
      this.mode = 'reconnecting';
      this.reconnecting = true;
      this.progress = 0;
      
      // Start progress animation
      this.progressInterval = setInterval(() => {
        if (this.progress < 90) {
          this.progress += Math.random() * 10;
        }
      }, 200);

      try {
        // Clear existing session
        this.brokerService.clearSession();
        
        // Initiate reconnection flow
        await this.initiateReconnection();
        
        // Complete progress
        this.progress = 100;
        setTimeout(() => {
          this.mode = 'success';
          this.reconnecting = false;
          clearInterval(this.progressInterval);
          this.$emit('reconnected', this.broker);
        }, 500);
        
      } catch (error) {
        console.error(`[${this.brokerName}] Reconnection failed:`, error);
        this.errorMessage = error.message || 'Reconnection failed';
        this.mode = 'error';
        this.reconnecting = false;
        clearInterval(this.progressInterval);
      }
    },
    
    async connect() {
      this.mode = 'reconnecting';
      this.reconnecting = true;
      this.progress = 0;
      
      // Start progress animation
      this.progressInterval = setInterval(() => {
        if (this.progress < 90) {
          this.progress += Math.random() * 10;
        }
      }, 200);

      try {
        await this.initiateReconnection();
        
        // Complete progress
        this.progress = 100;
        setTimeout(() => {
          this.mode = 'success';
          this.reconnecting = false;
          clearInterval(this.progressInterval);
          this.$emit('connected', this.broker);
        }, 500);
        
      } catch (error) {
        console.error(`[${this.brokerName}] Connection failed:`, error);
        this.errorMessage = error.message || 'Connection failed';
        this.mode = 'error';
        this.reconnecting = false;
        clearInterval(this.progressInterval);
      }
    },
    
    async initiateReconnection() {
      // Auto-reconnect using stored credentials:
      // The server already knows the user's API key/secret (encrypted in DB).
      // We just need to start the OAuth flow — the server will use stored credentials
      // to build the auth URL and exchange the code for a token.
      const authUrl = await this.brokerService.getAuthUrl();

      // Open OAuth popup
      const popup = window.open(authUrl, 'broker_reconnect', 'width=600,height=700,scrollbars=yes,resizable=yes');

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }

      return new Promise((resolve, reject) => {
        // Poll for popup close and complete login
        const pollInterval = setInterval(async () => {
          try {
            if (!popup || popup.closed) {
              clearInterval(pollInterval);

              // Small delay to ensure callback has been processed
              await new Promise(r => setTimeout(r, 1500));

              try {
                await this.brokerService.completeLogin();
                resolve();
              } catch (completeErr) {
                reject(new Error(completeErr.response?.data?.error || completeErr.message || 'Login was not completed.'));
              }
            }
          } catch (e) {
            // Cross-origin access error is expected while popup is on broker domain
          }
        }, 1000);

        // Safety timeout — stop polling after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          reject(new Error('Login timed out. Please try again.'));
        }, 300000);
      });
    },
    
    retry() {
      this.errorMessage = '';
      if (this.mode === 'error') {
        this.reconnect();
      }
    },
    
    dismiss() {
      this.$emit('dismiss');
    },
    
    reset() {
      this.mode = 'reconnect';
      this.reconnecting = false;
      this.progress = 0;
      this.errorMessage = '';
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
    }
  },
  
  beforeDestroy() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
    }
  }
};
</script>

<style scoped>
.broker-reconnect-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
}

.broker-reconnect-modal {
  background: #ffffff;
  border-radius: 12px;
  padding: 32px;
  max-width: 480px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  position: relative;
}

.broker-reconnect-modal.dark {
  background: #1a1f2e;
  color: #e5e7eb;
}

.broker-reconnect-close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.broker-reconnect-close:hover {
  background: rgba(0, 0, 0, 0.1);
  color: #374151;
}

.broker-reconnect-modal.dark .broker-reconnect-close:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #d1d5db;
}

.broker-reconnect-body {
  text-align: center;
}

.broker-reconnect-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.broker-reconnect-icon.warning {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.broker-reconnect-icon.info {
  background: rgba(41, 98, 255, 0.1);
  color: #2962ff;
}

.broker-reconnect-icon.success {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.broker-reconnect-icon.error {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.broker-reconnect-icon.loading {
  background: rgba(41, 98, 255, 0.1);
  color: #2962ff;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(41, 98, 255, 0.2);
  border-top: 3px solid #2962ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.broker-reconnect-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 16px;
  color: #111827;
}

.broker-reconnect-modal.dark .broker-reconnect-title {
  color: #f9fafb;
}

.broker-reconnect-desc {
  font-size: 16px;
  line-height: 1.6;
  color: #6b7280;
  margin: 0 0 32px;
}

.broker-reconnect-modal.dark .broker-reconnect-desc {
  color: #9ca3af;
}

.broker-reconnect-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.broker-reconnect-btn {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
}

.broker-reconnect-btn.primary {
  background: #2962ff;
  color: white;
}

.broker-reconnect-btn.primary:hover:not(:disabled) {
  background: #1d4ed8;
}

.broker-reconnect-btn.primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.broker-reconnect-btn.secondary {
  background: transparent;
  color: #6b7280;
  border: 1px solid #d1d5db;
}

.broker-reconnect-btn.secondary:hover:not(:disabled) {
  background: #f9fafb;
  color: #374151;
}

.broker-reconnect-modal.dark .broker-reconnect-btn.secondary {
  color: #9ca3af;
  border-color: #374151;
}

.broker-reconnect-modal.dark .broker-reconnect-btn.secondary:hover:not(:disabled) {
  background: #374151;
  color: #d1d5db;
}

.broker-reconnect-progress {
  margin: 24px 0;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}

.broker-reconnect-modal.dark .progress-bar {
  background: #374151;
}

.progress-fill {
  height: 100%;
  background: #2962ff;
  transition: width 0.3s ease;
  border-radius: 3px;
}

/* Transitions */
.broker-reconnect-fade-enter-active,
.broker-reconnect-fade-leave-active {
  transition: opacity 0.3s ease;
}

.broker-reconnect-fade-enter,
.broker-reconnect-fade-leave-to {
  opacity: 0;
}
</style>
